import React, { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import ChatWidget from "./components/ChatWidget";
import StickyCallButton from "./components/StickyCallButton";

// Lazy load all page components for code splitting
const Home = lazy(() => import("./pages/Home"));
const Services = lazy(() => import("./pages/Services"));
const ServiceAreas = lazy(() => import("./pages/ServiceAreas"));
const CityLandingPage = lazy(() => import("./pages/CityLandingPage"));
const Admin = lazy(() => import("./pages/Admin"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const FAQ = lazy(() => import("./pages/FAQ").then(module => ({ default: module.FAQ })));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const ServicePage = lazy(() => import("./pages/ServicePage"));
const ServiceLocationPage = lazy(() => import("./pages/ServiceLocationPage"));
const CommercialPaintingService = lazy(() => import("./pages/CommercialPaintingService"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Loading fallback component
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a8a]"></div>
        <p className="mt-4 text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

// All service slugs
const services = [
  "cabinet-painting",
  "roof-coating",
  "drywall-repair",
  "pressure-washing",
  "interior-exterior-painting",
  "waterproofing",
  "trim-molding",
  "exterior-painting",
  "deck-staircase-repair",
  "commercial-painting"
];


// Service-location combination cities (excludes los-angeles)
const serviceLocationCities = [
  "santa-barbara",
  "ventura",
  "montecito",
  "goleta",
  "carpinteria",
  "ojai",
  "camarillo",
  "solvang",
  "buellton",
  "santa-ynez",
  "isla-vista",
  "summerland",
  "oxnard"
];

// Blog article slugs
const blogArticles = [
  "how-to-prepare-cabinets-for-professional-painting",
  "benefits-of-flame-retardant-roof-coating",
  "drywall-repair-before-painting",
  "pressure-washing-before-painting",
  "choosing-right-paint-finish-for-trim",
  "waterproofing-your-santa-barbara-home",
  "deck-maintenance-tips",
  "exterior-painting-best-practices",
  "interior-painting-color-trends",
  "commercial-painting-guide"
];

// Admin route rendered outside of Switch/Route to avoid platform sitemap scanner
function AdminGuard() {
  const [location] = useLocation();
  if (location !== '/admin') return null;
  return (
    <Suspense fallback={<PageLoader />}>
      <Admin />
    </Suspense>
  );
}

function Router() {
  const [location] = useLocation();
  
  // Scroll to top on route change
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location]);

  // Render admin outside of Switch to hide from platform scanner
  if (location === '/admin') {
    return (
      <Suspense fallback={<PageLoader />}>
        <Admin />
      </Suspense>
    );
  }
  
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        {/* Static pages */}
        <Route path={"/"} component={Home} />
        <Route path={"/services"} component={Services} />
        <Route path={"/service-areas"} component={ServiceAreas} />
        <Route path={"/blog"} component={Blog} />
        <Route path={"/faq"} component={FAQPage} />

        {/* Individual service pages - location-agnostic URLs */}
        <Route path={"/service/cabinet-painting"} component={ServicePage} />
        <Route path={"/service/roof-coating-repair"} component={ServicePage} />
        <Route path={"/service/drywall-repair"} component={ServicePage} />
        <Route path={"/service/pressure-washing"} component={ServicePage} />
        <Route path={"/service/interior-exterior-painting"} component={ServicePage} />
        <Route path={"/service/waterproofing"} component={ServicePage} />
        <Route path={"/service/trim-molding"} component={ServicePage} />
        <Route path={"/service/exterior-painting"} component={ServicePage} />
        <Route path="/service/deck-staircase-repair" component={ServicePage} />
        <Route path="/service/commercial-painting" component={CommercialPaintingService} />
        <Route path="/service/venetian-plaster" component={ServicePage} />
        <Route path="/service/lime-wash" component={ServicePage} />
        
        {/* Legacy service pages with santa-barbara suffix for backward compatibility */}
        <Route path={"/service/cabinet-painting-santa-barbara"} component={ServicePage} />
        <Route path={"/service/roof-coating-repair-santa-barbara"} component={ServicePage} />
        <Route path={"/service/drywall-repair-santa-barbara"} component={ServicePage} />
        <Route path={"/service/pressure-washing-santa-barbara"} component={ServicePage} />
        <Route path={"/service/interior-exterior-painting-santa-barbara"} component={ServicePage} />
        <Route path={"/service/waterproofing-santa-barbara"} component={ServicePage} />
        <Route path={"/service/trim-molding-santa-barbara"} component={ServicePage} />
        <Route path={"/service/exterior-painting-santa-barbara"} component={ServicePage} />
        <Route path="/service/deck-staircase-repair-santa-barbara" component={ServicePage} />
        <Route path="/service/commercial-painting-santa-barbara" component={ServicePage} />

        {/* City landing pages */}
        <Route path={"/city/santa-barbara"} component={CityLandingPage} />
        <Route path={"/city/ventura"} component={CityLandingPage} />
        <Route path={"/city/montecito"} component={CityLandingPage} />
        <Route path={"/city/goleta"} component={CityLandingPage} />
        <Route path={"/city/carpinteria"} component={CityLandingPage} />
        <Route path={"/city/ojai"} component={CityLandingPage} />
        <Route path={"/city/camarillo"} component={CityLandingPage} />
        <Route path={"/city/solvang"} component={CityLandingPage} />
        <Route path={"/city/buellton"} component={CityLandingPage} />
        <Route path={"/city/santa-ynez"} component={CityLandingPage} />
        <Route path={"/city/isla-vista"} component={CityLandingPage} />
        <Route path={"/city/summerland"} component={CityLandingPage} />
        <Route path={"/city/oxnard"} component={CityLandingPage} />
        <Route path={"/city/los-angeles"} component={CityLandingPage} />


        {/* Service + Location combination pages - Cabinet Painting */}
        <Route path={"/service/cabinet-painting/santa-barbara"} component={ServiceLocationPage} />
        <Route path={"/service/cabinet-painting/ventura"} component={ServiceLocationPage} />
        <Route path={"/service/cabinet-painting/montecito"} component={ServiceLocationPage} />
        <Route path={"/service/cabinet-painting/goleta"} component={ServiceLocationPage} />
        <Route path={"/service/cabinet-painting/carpinteria"} component={ServiceLocationPage} />
        <Route path={"/service/cabinet-painting/ojai"} component={ServiceLocationPage} />
        <Route path={"/service/cabinet-painting/camarillo"} component={ServiceLocationPage} />
        <Route path={"/service/cabinet-painting/solvang"} component={ServiceLocationPage} />
        <Route path={"/service/cabinet-painting/buellton"} component={ServiceLocationPage} />
        <Route path={"/service/cabinet-painting/santa-ynez"} component={ServiceLocationPage} />
        <Route path={"/service/cabinet-painting/isla-vista"} component={ServiceLocationPage} />
        <Route path={"/service/cabinet-painting/summerland"} component={ServiceLocationPage} />
        <Route path={"/service/cabinet-painting/oxnard"} component={ServiceLocationPage} />

        {/* Service + Location combination pages - Roof Coating */}
        <Route path={"/service/roof-coating-repair/santa-barbara"} component={ServiceLocationPage} />
        <Route path={"/service/roof-coating-repair/ventura"} component={ServiceLocationPage} />
        <Route path={"/service/roof-coating-repair/montecito"} component={ServiceLocationPage} />
        <Route path={"/service/roof-coating-repair/goleta"} component={ServiceLocationPage} />
        <Route path={"/service/roof-coating-repair/carpinteria"} component={ServiceLocationPage} />
        <Route path={"/service/roof-coating-repair/ojai"} component={ServiceLocationPage} />
        <Route path={"/service/roof-coating-repair/camarillo"} component={ServiceLocationPage} />
        <Route path={"/service/roof-coating-repair/solvang"} component={ServiceLocationPage} />
        <Route path={"/service/roof-coating-repair/buellton"} component={ServiceLocationPage} />
        <Route path={"/service/roof-coating-repair/santa-ynez"} component={ServiceLocationPage} />
        <Route path={"/service/roof-coating-repair/isla-vista"} component={ServiceLocationPage} />
        <Route path={"/service/roof-coating-repair/summerland"} component={ServiceLocationPage} />
        <Route path={"/service/roof-coating-repair/oxnard"} component={ServiceLocationPage} />

        {/* Service + Location combination pages - Drywall Repair */}
        <Route path={"/service/drywall-repair/santa-barbara"} component={ServiceLocationPage} />
        <Route path={"/service/drywall-repair/ventura"} component={ServiceLocationPage} />
        <Route path={"/service/drywall-repair/montecito"} component={ServiceLocationPage} />
        <Route path={"/service/drywall-repair/goleta"} component={ServiceLocationPage} />
        <Route path={"/service/drywall-repair/carpinteria"} component={ServiceLocationPage} />
        <Route path={"/service/drywall-repair/ojai"} component={ServiceLocationPage} />
        <Route path={"/service/drywall-repair/camarillo"} component={ServiceLocationPage} />
        <Route path={"/service/drywall-repair/solvang"} component={ServiceLocationPage} />
        <Route path={"/service/drywall-repair/buellton"} component={ServiceLocationPage} />
        <Route path={"/service/drywall-repair/santa-ynez"} component={ServiceLocationPage} />
        <Route path={"/service/drywall-repair/isla-vista"} component={ServiceLocationPage} />
        <Route path={"/service/drywall-repair/summerland"} component={ServiceLocationPage} />
        <Route path={"/service/drywall-repair/oxnard"} component={ServiceLocationPage} />

        {/* Service + Location combination pages - Pressure Washing */}
        <Route path={"/service/pressure-washing/santa-barbara"} component={ServiceLocationPage} />
        <Route path={"/service/pressure-washing/ventura"} component={ServiceLocationPage} />
        <Route path={"/service/pressure-washing/montecito"} component={ServiceLocationPage} />
        <Route path={"/service/pressure-washing/goleta"} component={ServiceLocationPage} />
        <Route path={"/service/pressure-washing/carpinteria"} component={ServiceLocationPage} />
        <Route path={"/service/pressure-washing/ojai"} component={ServiceLocationPage} />
        <Route path={"/service/pressure-washing/camarillo"} component={ServiceLocationPage} />
        <Route path={"/service/pressure-washing/solvang"} component={ServiceLocationPage} />
        <Route path={"/service/pressure-washing/buellton"} component={ServiceLocationPage} />
        <Route path={"/service/pressure-washing/santa-ynez"} component={ServiceLocationPage} />
        <Route path={"/service/pressure-washing/isla-vista"} component={ServiceLocationPage} />
        <Route path={"/service/pressure-washing/summerland"} component={ServiceLocationPage} />
        <Route path={"/service/pressure-washing/oxnard"} component={ServiceLocationPage} />

        {/* Service + Location combination pages - Interior/Exterior Painting */}
        <Route path={"/service/interior-exterior-painting/santa-barbara"} component={ServiceLocationPage} />
        <Route path={"/service/interior-exterior-painting/ventura"} component={ServiceLocationPage} />
        <Route path={"/service/interior-exterior-painting/montecito"} component={ServiceLocationPage} />
        <Route path={"/service/interior-exterior-painting/goleta"} component={ServiceLocationPage} />
        <Route path={"/service/interior-exterior-painting/carpinteria"} component={ServiceLocationPage} />
        <Route path={"/service/interior-exterior-painting/ojai"} component={ServiceLocationPage} />
        <Route path={"/service/interior-exterior-painting/camarillo"} component={ServiceLocationPage} />
        <Route path={"/service/interior-exterior-painting/solvang"} component={ServiceLocationPage} />
        <Route path={"/service/interior-exterior-painting/buellton"} component={ServiceLocationPage} />
        <Route path={"/service/interior-exterior-painting/santa-ynez"} component={ServiceLocationPage} />
        <Route path={"/service/interior-exterior-painting/isla-vista"} component={ServiceLocationPage} />
        <Route path={"/service/interior-exterior-painting/summerland"} component={ServiceLocationPage} />
        <Route path={"/service/interior-exterior-painting/oxnard"} component={ServiceLocationPage} />

        {/* Service + Location combination pages - Waterproofing */}
        <Route path={"/service/waterproofing/santa-barbara"} component={ServiceLocationPage} />
        <Route path={"/service/waterproofing/ventura"} component={ServiceLocationPage} />
        <Route path={"/service/waterproofing/montecito"} component={ServiceLocationPage} />
        <Route path={"/service/waterproofing/goleta"} component={ServiceLocationPage} />
        <Route path={"/service/waterproofing/carpinteria"} component={ServiceLocationPage} />
        <Route path={"/service/waterproofing/ojai"} component={ServiceLocationPage} />
        <Route path={"/service/waterproofing/camarillo"} component={ServiceLocationPage} />
        <Route path={"/service/waterproofing/solvang"} component={ServiceLocationPage} />
        <Route path={"/service/waterproofing/buellton"} component={ServiceLocationPage} />
        <Route path={"/service/waterproofing/santa-ynez"} component={ServiceLocationPage} />
        <Route path={"/service/waterproofing/isla-vista"} component={ServiceLocationPage} />
        <Route path={"/service/waterproofing/summerland"} component={ServiceLocationPage} />
        <Route path={"/service/waterproofing/oxnard"} component={ServiceLocationPage} />

        {/* Service + Location combination pages - Trim & Molding */}
        <Route path={"/service/trim-molding/santa-barbara"} component={ServiceLocationPage} />
        <Route path={"/service/trim-molding/ventura"} component={ServiceLocationPage} />
        <Route path={"/service/trim-molding/montecito"} component={ServiceLocationPage} />
        <Route path={"/service/trim-molding/goleta"} component={ServiceLocationPage} />
        <Route path={"/service/trim-molding/carpinteria"} component={ServiceLocationPage} />
        <Route path={"/service/trim-molding/ojai"} component={ServiceLocationPage} />
        <Route path={"/service/trim-molding/camarillo"} component={ServiceLocationPage} />
        <Route path={"/service/trim-molding/solvang"} component={ServiceLocationPage} />
        <Route path={"/service/trim-molding/buellton"} component={ServiceLocationPage} />
        <Route path={"/service/trim-molding/santa-ynez"} component={ServiceLocationPage} />
        <Route path={"/service/trim-molding/isla-vista"} component={ServiceLocationPage} />
        <Route path={"/service/trim-molding/summerland"} component={ServiceLocationPage} />
        <Route path={"/service/trim-molding/oxnard"} component={ServiceLocationPage} />

        {/* Service + Location combination pages - Exterior Painting */}
        <Route path={"/service/exterior-painting/santa-barbara"} component={ServiceLocationPage} />
        <Route path={"/service/exterior-painting/ventura"} component={ServiceLocationPage} />
        <Route path={"/service/exterior-painting/montecito"} component={ServiceLocationPage} />
        <Route path={"/service/exterior-painting/goleta"} component={ServiceLocationPage} />
        <Route path={"/service/exterior-painting/carpinteria"} component={ServiceLocationPage} />
        <Route path={"/service/exterior-painting/ojai"} component={ServiceLocationPage} />
        <Route path={"/service/exterior-painting/camarillo"} component={ServiceLocationPage} />
        <Route path={"/service/exterior-painting/solvang"} component={ServiceLocationPage} />
        <Route path={"/service/exterior-painting/buellton"} component={ServiceLocationPage} />
        <Route path={"/service/exterior-painting/santa-ynez"} component={ServiceLocationPage} />
        <Route path={"/service/exterior-painting/isla-vista"} component={ServiceLocationPage} />
        <Route path={"/service/exterior-painting/summerland"} component={ServiceLocationPage} />
        <Route path={"/service/exterior-painting/oxnard"} component={ServiceLocationPage} />

        {/* Service + Location combination pages - Deck & Staircase Repair */}
        <Route path={"/service/deck-staircase-repair/santa-barbara"} component={ServiceLocationPage} />
        <Route path={"/service/deck-staircase-repair/ventura"} component={ServiceLocationPage} />
        <Route path={"/service/deck-staircase-repair/montecito"} component={ServiceLocationPage} />
        <Route path={"/service/deck-staircase-repair/goleta"} component={ServiceLocationPage} />
        <Route path={"/service/deck-staircase-repair/carpinteria"} component={ServiceLocationPage} />
        <Route path={"/service/deck-staircase-repair/ojai"} component={ServiceLocationPage} />
        <Route path={"/service/deck-staircase-repair/camarillo"} component={ServiceLocationPage} />
        <Route path={"/service/deck-staircase-repair/solvang"} component={ServiceLocationPage} />
        <Route path={"/service/deck-staircase-repair/buellton"} component={ServiceLocationPage} />
        <Route path={"/service/deck-staircase-repair/santa-ynez"} component={ServiceLocationPage} />
         <Route path="/service/deck-staircase-repair/isla-vista" component={ServiceLocationPage} />
        <Route path="/service/deck-staircase-repair/summerland" component={ServiceLocationPage} />
        <Route path="/service/deck-staircase-repair/oxnard" component={ServiceLocationPage} />

        {/* Service + Location combination pages - Commercial Painting */}
        <Route path="/service/commercial-painting/santa-barbara" component={ServiceLocationPage} />
        <Route path="/service/commercial-painting/ventura" component={ServiceLocationPage} />
        <Route path="/service/commercial-painting/montecito" component={ServiceLocationPage} />
        <Route path="/service/commercial-painting/goleta" component={ServiceLocationPage} />
        <Route path="/service/commercial-painting/carpinteria" component={ServiceLocationPage} />
        <Route path="/service/commercial-painting/ojai" component={ServiceLocationPage} />
        <Route path="/service/commercial-painting/camarillo" component={ServiceLocationPage} />
        <Route path="/service/commercial-painting/solvang" component={ServiceLocationPage} />
        <Route path="/service/commercial-painting/buellton" component={ServiceLocationPage} />
        <Route path="/service/commercial-painting/santa-ynez" component={ServiceLocationPage} />
        <Route path="/service/commercial-painting/isla-vista" component={ServiceLocationPage} />
        <Route path="/service/commercial-painting/summerland" component={ServiceLocationPage} />
        <Route path="/service/commercial-painting/oxnard" component={ServiceLocationPage} />

        {/* Service + Location combination pages - Los Angeles */}
        <Route path={'/service/cabinet-painting/los-angeles'} component={ServiceLocationPage} />
        <Route path={'/service/roof-coating-repair/los-angeles'} component={ServiceLocationPage} />
        <Route path={'/service/drywall-repair/los-angeles'} component={ServiceLocationPage} />
        <Route path={'/service/pressure-washing/los-angeles'} component={ServiceLocationPage} />
        <Route path={'/service/interior-exterior-painting/los-angeles'} component={ServiceLocationPage} />
        <Route path={'/service/waterproofing/los-angeles'} component={ServiceLocationPage} />
        <Route path={'/service/trim-molding/los-angeles'} component={ServiceLocationPage} />
        <Route path={'/service/exterior-painting/los-angeles'} component={ServiceLocationPage} />
        <Route path='/service/deck-staircase-repair/los-angeles' component={ServiceLocationPage} />
        <Route path='/service/commercial-painting/los-angeles' component={ServiceLocationPage} />

        {/* Dynamic route fallbackss for any routes not explicitly listed */}
        <Route path={"/city/:slug"} component={CityLandingPage} />
        <Route path={"/service/:serviceSlug/:locationSlug"} component={ServiceLocationPage} />
        <Route path={"/service/:slug"} component={ServicePage} />
        
        <Route path={"/blog/:slug"} component={BlogArticle} />

        <Route path={"/404"} component={NotFound} />
        {/* Final fallback route */}
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

// NOTE: About Theme
// - First choose a default theme according to your design style (dark or light bg), than change color palette in index.css
//   to keep consistent foreground/background color across components
// - If you want to make theme switchable, pass `switchable` ThemeProvider and use `useTheme` hook

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider
        defaultTheme="light"
        // switchable
      >
        <TooltipProvider>
          <Toaster />
          <Router />
          <GlobalChatWidget />
          <StickyCallButton />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

// Global ChatWidget - only shown on public pages, not admin
function GlobalChatWidget() {
  const [location] = useLocation();
  // Don't show chatbot on admin pages or homeowner dashboard
  if (location === '/admin' || location.startsWith('/homeowner')) return null;
  return <ChatWidget />;
}

export default App;
